import { useState } from "react";
import { createPortal } from "react-dom";
import type { AssetPair } from "../../swapkit/helpers.ts";

// ---------------------------------------------------------------------------
// Inline token icons — fully solid, no transparency
// ---------------------------------------------------------------------------

function EthIcon({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      {/* Upper body */}
      <path d="M16 7L10 17L16 20.5L22 17Z" fill="white" />
      <path d="M16 7L10 17L16 20.5Z" fill="rgba(255,255,255,0.75)" />
      {/* Lower body */}
      <path d="M10 18.5L16 22L22 18.5L16 26Z" fill="white" />
      <path d="M10 18.5L16 26L16 22Z" fill="rgba(255,255,255,0.75)" />
    </svg>
  );
}

function UsdcIcon({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#2775CA" />
      <circle cx="16" cy="16" r="10" fill="none" stroke="white" strokeWidth="1.5" />
      <path
        d="M18.5 12.5C18.5 11.1 17.4 10 16 10C14.6 10 13.5 11.1 13.5 12.5C13.5 13.9 14.6 15 16 15C17.4 15 18.5 16.1 18.5 17.5C18.5 18.9 17.4 20 16 20C14.6 20 13.5 18.9 13.5 17.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line x1="16" y1="9" x2="16" y2="11" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="16" y1="21" x2="16" y2="23" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function BaseChainIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="7" fill="#0052FF" />
      <path
        d="M7 3.5C5.07 3.5 3.5 5.07 3.5 7C3.5 8.93 5.07 10.5 7 10.5C8.83 10.5 10.35 9.08 10.49 7.28H6.72V6.72H10.49C10.35 4.92 8.83 3.5 7 3.5Z"
        fill="white"
      />
    </svg>
  );
}

function getTokenIcon(asset: string, size: number) {
  if (asset.includes("USDC")) return <UsdcIcon size={size} />;
  return <EthIcon size={size} />;
}

function getChainIcon(chainId: string, size: number) {
  if (chainId === "8453") return <BaseChainIcon size={size} />;
  return null;
}

export function getSymbol(asset: string): string {
  const part = asset.split(".")[1] ?? asset;
  return part.split("-")[0]!;
}

function getChainName(chainId: string): string {
  if (chainId === "8453") return "Base";
  if (chainId === "1") return "Ethereum";
  if (chainId === "42161") return "Arbitrum";
  return chainId;
}

// ---------------------------------------------------------------------------
// Token badge — the clickable selector button
// ---------------------------------------------------------------------------

interface AssetSelectorProps {
  assets: AssetPair[];
  selected: AssetPair;
  onChange: (a: AssetPair) => void;
  disabled?: boolean;
}

export function AssetSelector({ assets, selected, onChange, disabled }: AssetSelectorProps) {
  const [open, setOpen] = useState(false);

  const symbol = getSymbol(selected.asset);
  const chainName = getChainName(selected.chainId);

  // ---------------------------------------------------------------------------
  // Modal — rendered via portal so `transform` on ancestors doesn't break
  // `position: fixed`. The badge sits inside a -translate-x-1/2 element,
  // which would otherwise anchor fixed descendants to that element's box.
  // ---------------------------------------------------------------------------
  const modal = open
    ? createPortal(
        <div
          className="fixed inset-0 z-9999 flex items-center justify-center bg-void/80 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-[#0d0d1a] border border-white/8 rounded-2xl p-5 w-full max-w-75 shadow-2xl shadow-black/60"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-ghost-white mb-4 text-sm">
              Select token
            </h3>

            <div className="flex flex-col gap-1.5">
              {assets.map((a) => {
                const sym = getSymbol(a.asset);
                const chain = getChainName(a.chainId);
                const isSelected = a.asset === selected.asset;
                return (
                  <button
                    key={a.asset}
                    type="button"
                    onClick={() => { onChange(a); setOpen(false); }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-colors text-left w-full ${
                      isSelected
                        ? "bg-spectral-violet/20 border border-spectral-violet/40"
                        : "bg-white/4 hover:bg-white/8 border border-transparent"
                    }`}
                  >
                    {/* Token icon with chain overlay */}
                    <div className="relative shrink-0">
                      {getTokenIcon(a.asset, 36)}
                      <div className="absolute -bottom-1 -left-1 rounded-full border-2 border-[#0d0d1a]">
                        {getChainIcon(a.chainId, 15)}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold text-ghost-white">{sym}</div>
                      <div className="text-xs text-ash">{chain}</div>
                    </div>

                    {isSelected && (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-spectral-violet shrink-0">
                        <path d="M3 8.5L6.5 12L13 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full mt-4 py-2.5 rounded-xl border border-white/10 text-mist font-heading text-sm hover:border-white/20 hover:text-ghost-white transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        className={`flex items-center gap-2.5 rounded-xl px-3 py-2 transition-colors border border-white/10 ${
          disabled ? "cursor-default bg-[#304257]" : "bg-[#304257] hover:bg-[#3d5068] cursor-pointer"
        }`}
        aria-label={`Select token — currently ${symbol}`}
      >
        {/* Token icon with chain overlay */}
        <div className="relative shrink-0">
          {getTokenIcon(selected.asset, 28)}
          <div className="absolute -bottom-1 -left-1 rounded-full border-2 border-[#304257]">
            {getChainIcon(selected.chainId, 13)}
          </div>
        </div>

        {/* Symbol + chain */}
        <div className="flex flex-col items-start leading-none">
          <span className="font-heading font-semibold text-ghost-white text-sm">{symbol}</span>
          <span className="text-[0.6rem] mt-0.5" style={{ color: "#5d6a7b" }}>{chainName}</span>
        </div>

        {!disabled && (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-mist ml-0.5">
            <path d="M5 4L8 7L5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {modal}
    </>
  );
}

export { getTokenIcon, getChainIcon, getChainName };
